import { CreateContactDialog } from "@/components/CreateContactDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Building2, Mail, Phone, Plus, Search, User } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

// ============================================================
// Contacts Page — Kontakte-Übersicht + Suche
// ============================================================

interface ContactListResponse {
	data: Array<{
		contact: {
			id: string;
			name: string;
			vorname: string | null;
			nachname: string | null;
			firma: string | null;
			email: string | null;
			telefon: string | null;
			position: string | null;
			ort: string | null;
			createdAt: string;
		};
		bookingCount: number;
	}>;
	total: number;
	limit: number;
	offset: number;
}

export function ContactsPage() {
	const [params, setParams] = useSearchParams();
	const [showDetail, setShowDetail] = useState<string | null>(null);
	const [showCreate, setShowCreate] = useState(false);

	const q = params.get("q") ?? "";
	const page = Number(params.get("page") ?? "0");

	const query = useQuery({
		queryKey: ["contacts", { q, page }],
		queryFn: () => {
			const p = new URLSearchParams();
			if (q) p.set("search", q);
			p.set("offset", String(page * 50));
			p.set("limit", "50");
			return api.get<ContactListResponse>(`/contacts?${p.toString()}`);
		},
	});

	const contacts = query.data?.data ?? [];
	const total = query.data?.total ?? 0;

	function setFilter(key: string, value: string) {
		const next = new URLSearchParams(params);
		if (value) next.set(key, value);
		else next.delete(key);
		next.delete("page");
		setParams(next);
	}

	return (
		<div className="space-y-4 animate-fade-in">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-display text-2xl">Kontakte</h1>
					<p className="text-sm text-stone-500">{total} gesamt</p>
				</div>
				<button
					type="button"
					onClick={() => setShowCreate(true)}
					className="btn-primary"
				>
					<Plus size={16} /> Neuer Kontakt
				</button>
			</div>

			{showCreate && (
				<CreateContactDialog onClose={() => setShowCreate(false)} />
			)}

			{/* Search */}
			<div className="relative">
				<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
				<input
					type="text"
					value={q}
					onChange={(e) => setFilter("q", e.target.value)}
					placeholder="Name, Firma, E-Mail suchen..."
					className="input pl-9"
				/>
			</div>


			{/* Error State */}
			{query.isError && (
				<div className="card border-red-200 bg-red-50 text-red-700 text-sm">
					Fehler beim Laden der Kontakte.{" "}
					<button type="button" onClick={() => query.refetch()} className="underline">
						Erneut versuchen
					</button>
				</div>
			)}

			{/* List */}
			<div className="space-y-2">
				{contacts.map(({ contact: c, bookingCount }) => (
					<div
						key={c.id}
						className="card hover:shadow-sm transition-shadow cursor-pointer"
						onClick={() => setShowDetail(showDetail === c.id ? null : c.id)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ")
								setShowDetail(showDetail === c.id ? null : c.id);
						}}
					>
						<div className="flex items-center gap-3">
							<div className="h-10 w-10 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
								<User size={18} />
							</div>
							<div className="flex-1 min-w-0">
								<p className="font-medium text-stone-900 truncate">{c.name}</p>
								<div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-stone-500">
									{c.firma && (
										<span className="inline-flex items-center gap-1">
											<Building2 size={10} /> {c.firma}
										</span>
									)}
									{c.position && <span>{c.position}</span>}
									{c.ort && <span>{c.ort}</span>}
								</div>
							</div>
							<div className="text-right shrink-0">
								<span className="badge bg-stone-100 text-stone-600">
									{bookingCount} Buchungen
								</span>
							</div>
						</div>

						{/* Expanded detail */}
						{showDetail === c.id && (
							<div className="mt-3 pt-3 border-t border-stone-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm animate-slide-up">
								{c.email && (
									<a
										href={`mailto:${c.email}`}
										className="flex items-center gap-2 text-brand-600 hover:underline"
									>
										<Mail size={14} /> {c.email}
									</a>
								)}
								{c.telefon && (
									<a
										href={`tel:${c.telefon}`}
										className="flex items-center gap-2 text-stone-700"
									>
										<Phone size={14} /> {c.telefon}
									</a>
								)}
								<p className="text-xs text-stone-400">
									Erstellt: {formatDate(c.createdAt)}
								</p>
							</div>
						)}
					</div>
				))}
				{contacts.length === 0 && !query.isLoading && (
					<div className="text-center py-12 text-stone-400">
						Keine Kontakte gefunden.
					</div>
				)}
			</div>

			{/* Pagination */}
			{total > 50 && (
				<div className="flex items-center justify-between">
					<span className="text-xs text-stone-500">
						Seite {page + 1} von {Math.ceil(total / 50)}
					</span>
					<div className="flex gap-2">
						<button
							type="button"
							disabled={page === 0}
							onClick={() => setFilter("page", String(page - 1))}
							className="btn-ghost text-xs disabled:opacity-30"
						>
							Zurück
						</button>
						<button
							type="button"
							disabled={(page + 1) * 50 >= total}
							onClick={() => setFilter("page", String(page + 1))}
							className="btn-ghost text-xs disabled:opacity-30"
						>
							Weiter
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
